import { useEffect, useMemo, useState } from 'react';
import { Listbox } from '@headlessui/react';
import { api, ApiError } from '../lib/api';
import { LoadingState, ErrorState } from './wizardStates';
import { CheckIcon, SpinnerIcon } from './icons';
import { formatPrice } from '../lib/format';
import type {
  ProductAction,
  ProductConfirmItem,
  PricePreviewItem,
  ProductPreviewResponse,
  HubSpotProduct,
  HubSpotProductsResponse,
} from './types';

/**
 * Step 1 — map Stripe prices to HubSpot products (M24: 1 Price = 1 HS Product).
 *
 * Fetches the price preview (Stripe prices + auto-matched HubSpot products by
 * derived name), then lets the admin choose per-price: Link (to an existing
 * HubSpot product) / Create (new HubSpot product) / Ignore. Prices are grouped
 * visually by their parent Stripe Product name.
 *
 * On confirm, POSTs the choices to /onboarding/confirm/products.
 */
export function StepProducts({ onDone, onBack }: { onDone: () => void; onBack?: () => void; }) {
  const [items, setItems] = useState<PricePreviewItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actions, setActions] = useState<Record<string, ProductAction>>({});
  const [linkSelections, setLinkSelections] = useState<Record<string, string>>({});
  const [hsProducts, setHsProducts] = useState<HubSpotProduct[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Fetch the price preview.
  useEffect(() => {
    let cancelled = false;
    api
      .get<ProductPreviewResponse>('onboarding/preview/products')
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        // Seed actions from suggestedAction.
        const seed: Record<string, ProductAction> = {};
        for (const item of data.items) {
          seed[item.stripePriceId] = item.suggestedAction;
        }
        setActions(seed);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : 'Failed to load products');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Lazy-load HubSpot products once the first price is set to 'link'.
  const anyLink = useMemo(
    () => Object.values(actions).some((a) => a === 'link'),
    [actions]
  );
  useEffect(() => {
    if (hsProducts !== null) return;
    if (!anyLink) return;
    let cancelled = false;
    api
      .get<HubSpotProductsResponse>('api/admin/hubspot/products')
      .then((data) => {
        if (!cancelled) setHsProducts(data.items);
      })
      .catch(() => {
        if (!cancelled) setHsProducts([]); // empty list, picker still works
      });
    return () => {
      cancelled = true;
    };
  }, [anyLink, hsProducts]);

  if (loading) return <LoadingState label="Loading your Stripe products…" />;
  if (error) return <ErrorState message={error} />;
  if (!items) return null;

  if (items.length === 0) {
    return (
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-ink-900">No prices to map</h2>
        <p className="mt-2 text-sm text-ink-500">
          Your Stripe account has no prices. You can skip this step and
          continue to the import rules — subscription sync will still work,
          matching subscriptions by customer email.
        </p>
        <button className="btn-primary mt-4" onClick={onDone}>
          Continue
        </button>
      </div>
    );
  }

  const counts = {
    link: items.filter((i) => actions[i.stripePriceId] === 'link').length,
    create: items.filter((i) => actions[i.stripePriceId] === 'create').length,
    ignore: items.filter((i) => actions[i.stripePriceId] === 'ignore').length,
  };

  // Group prices by productName for display (subheader per product).
  // Plain computation (not useMemo) — must run AFTER the early returns above,
  // so it can't be a hook (Rules of Hooks: hooks can't be conditional).
  const grouped: [string, PricePreviewItem[]][] = (() => {
    const map = new Map<string, PricePreviewItem[]>();
    for (const item of items) {
      const key = item.productName;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries());
  })();

  const handleConfirm = async () => {
    // Validate: every 'link' price must have a selected HubSpot product.
    const missing = items.find(
      (i) =>
        actions[i.stripePriceId] === 'link' &&
        !linkSelections[i.stripePriceId] &&
        !i.hubspotMatch.productId
    );
    if (missing) {
      setConfirmError(`Select a HubSpot product to link "${missing.derivedHubSpotName}" before continuing.`);
      return;
    }
    setConfirming(true);
    setConfirmError(null);
    try {
      const confirmItems: ProductConfirmItem[] = items.map((i) => {
        const a = actions[i.stripePriceId] ?? 'ignore';
        const picked =
          linkSelections[i.stripePriceId] ?? i.hubspotMatch.productId ?? undefined;
        return {
          stripePriceId: i.stripePriceId,
          action: a,
          hubspotProductId: a === 'link' ? picked : undefined,
        };
      });
      await api.post('onboarding/confirm/products', { items: confirmItems });
      onDone();
    } catch (e) {
      setConfirmError(e instanceof ApiError ? e.message : 'Failed to save');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-ink-900">Map your prices ({items.length})</h2>
        <p className="mt-1 text-sm text-ink-500">
          Each Stripe price maps to a HubSpot product. <strong>Link</strong> an
          existing one, <strong>Create</strong> a new one, or <strong>Ignore</strong>.
        </p>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-200 bg-ink-50 text-left text-xs font-medium uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-4 py-2.5">Product / Price</th>
              <th className="px-4 py-2.5">Derived Name</th>
              <th className="px-4 py-2.5">Match</th>
              <th className="px-4 py-2.5">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {grouped.map(([productName, prices]) => (
              <ProductGroup
                key={productName}
                productName={productName}
                prices={prices}
                actions={actions}
                hsProducts={hsProducts}
                linkSelections={linkSelections}
                onAction={(priceId, a) => setActions({ ...actions, [priceId]: a })}
                onSelectProduct={(priceId, id) => setLinkSelections({ ...linkSelections, [priceId]: id })}
              />
            ))}
          </tbody>
        </table>
      </div>

      {confirmError && (
        <p className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-600">
          {confirmError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-sm font-medium text-ink-400 transition-colors hover:text-ink-700"
          >
            ← Back
          </button>
        )}
        <button className="btn-primary" onClick={handleConfirm} disabled={confirming}>
          {confirming ? (
            <>
              <SpinnerIcon className="h-4 w-4" /> Saving…
            </>
          ) : (
            `Continue (${counts.link} link, ${counts.create} create, ${counts.ignore} ignore)`
          )}
        </button>
      </div>
    </div>
  );
}

/** A group of prices for the same Stripe Product, with a subheader row. */
function ProductGroup({
  productName,
  prices,
  actions,
  hsProducts,
  linkSelections,
  onAction,
  onSelectProduct,
}: {
  productName: string;
  prices: PricePreviewItem[];
  actions: Record<string, ProductAction>;
  hsProducts: HubSpotProduct[] | null;
  linkSelections: Record<string, string>;
  onAction: (priceId: string, a: ProductAction) => void;
  onSelectProduct: (priceId: string, id: string) => void;
}) {
  return (
    <>
      <tr className="bg-ink-50/50">
        <td colSpan={4} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-ink-600">
          {productName}
        </td>
      </tr>
      {prices.map((item) => (
        <PriceRow
          key={item.stripePriceId}
          item={item}
          action={actions[item.stripePriceId] ?? 'create'}
          hsProducts={hsProducts}
          selectedProductId={linkSelections[item.stripePriceId]}
          onAction={(a) => onAction(item.stripePriceId, a)}
          onSelectProduct={(id) => onSelectProduct(item.stripePriceId, id)}
        />
      ))}
    </>
  );
}

/** One price row with its action selector + (conditional) HubSpot picker. */
function PriceRow({
  item,
  action,
  hsProducts,
  selectedProductId,
  onAction,
  onSelectProduct,
}: {
  item: PricePreviewItem;
  action: ProductAction;
  hsProducts: HubSpotProduct[] | null;
  selectedProductId?: string;
  onAction: (a: ProductAction) => void;
  onSelectProduct: (id: string) => void;
}) {
  const priceText = formatPrice(item.amountMinor, item.currency, item.interval, item.intervalCount);

  return (
    <tr className="align-top">
      <td className="px-4 py-3 pl-8 text-ink-600">
        {priceText}
        {!item.active && <span className="badge-warn ml-2">archived</span>}
      </td>
      <td className="px-4 py-3">
        <div className="font-medium text-ink-800">{item.derivedHubSpotName}</div>
      </td>
      <td className="px-4 py-3">
        {item.hubspotMatch.productId ? (
          <span className="badge-success">
            <CheckIcon className="h-3 w-3" /> #{item.hubspotMatch.productId}
          </span>
        ) : (
          <span className="text-ink-400">no match</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-2">
          <ActionToggle action={action} onChange={onAction} />
          {action === 'link' && (
            <ProductPicker
              products={hsProducts}
              selectedId={selectedProductId ?? item.hubspotMatch.productId ?? null}
              onSelect={onSelectProduct}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

/** The Link / Create / Ignore pill toggle. */
function ActionToggle({
  action,
  onChange,
}: {
  action: ProductAction;
  onChange: (a: ProductAction) => void;
}) {
  const opts: { id: ProductAction; label: string; }[] = [
    { id: 'link', label: 'Link' },
    { id: 'create', label: 'Create' },
    { id: 'ignore', label: 'Ignore' },
  ];
  return (
    <div className="inline-flex rounded-lg border border-ink-200 bg-ink-50 p-0.5">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={[
            'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            action === o.id
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-ink-500 hover:text-ink-700',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Searchable HubSpot product picker using Headless UI Listbox. Shows a loading
 * state while products are fetching, and an empty-state if there are none.
 */
function ProductPicker({
  products,
  selectedId,
  onSelect,
}: {
  products: HubSpotProduct[] | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (products === null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-ink-400">
        <SpinnerIcon className="h-3 w-3" /> loading products…
      </span>
    );
  }
  if (products.length === 0) {
    return <span className="text-xs text-ink-400">No existing HubSpot products — use Create.</span>;
  }

  const selected = products.find((p) => p.id === selectedId) ?? null;
  // Headless UI Listbox expects `string | undefined` for value; coerce null.
  const listboxValue = selectedId ?? undefined;
  return (
    <div className="relative w-64">
      <Listbox value={listboxValue} onChange={onSelect}>
        <Listbox.Button className="input flex items-center justify-between text-left">
          <span className={selected ? 'text-ink-800' : 'text-ink-400'}>
            {selected ? `${selected.name} (#${selected.id})` : 'Select a HubSpot product…'}
          </span>
          <svg className="h-4 w-4 text-ink-400" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.3 7.3a1 1 0 0 1 1.4 0L10 10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4Z"
              clipRule="evenodd"
            />
          </svg>
        </Listbox.Button>
        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-ink-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
          {products.map((p) => (
            <Listbox.Option
              key={p.id}
              value={p.id}
              className={({ active }) =>
                [
                  'flex cursor-pointer items-center justify-between px-3 py-1.5',
                  active ? 'bg-brand-50 text-brand-700' : 'text-ink-700',
                ].join(' ')
              }
            >
              {({ selected }) => (
                <>
                  <span className="truncate">
                    {p.name} <span className="text-ink-400">#{p.id}</span>
                  </span>
                  {selected && <CheckIcon className="h-4 w-4 flex-shrink-0 text-brand-600" />}
                </>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Listbox>
    </div>
  );
}
